import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { QbeIcons } from 'qbe-components';
import { Observable, Subject, combineLatest, filter, first, map, takeUntil } from 'rxjs';
import { PathPayload } from 'src/app/model/path.model';
import { AgentSaveCalculationPayload } from 'src/app/model/save-calculation.model';
import { FormsService } from 'src/app/services/forms.service';
import { RadioButton } from '../../component/radio-buttons/radio-buttons.component';
import { environment } from '../../environments/environment';
import { ApiState } from '../../model/api-state.model';
import { AppType } from '../../model/app-type.model';
import { FormOfPayment } from '../../model/purchase-agent.model';
import { ProcessService, ProcessType } from '../../services/process.service';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';

interface HorizontalButton extends RadioButton {
  value: FormOfPayment;
}

const MIN_SPLITS_REQUIRED = 1;

@Component({
  selector: 'app-page-conclusion-agent',
  templateUrl: './conclusion-agent.component.html',
  styleUrls: ['./conclusion-agent.component.scss'],
})
export class ConclusionAgentComponent implements OnInit, OnDestroy {
  isTravelAgency = environment.app === AppType.TravelAgency;
  nextStepUrl$ = this.store.select(fromProcess.selectProcessNextStepUrl);
  conclusionStatus$ = this.store.select(fromProcess.selectProcessConclusionState);
  formOfPayment$ = this.store.select(fromProcess.selectProcessFormOfPayment);
  ngDestroy$ = new Subject<void>();
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  selectedPackage$ = this.store.select(fromProcess.selectProcessPackageName);
  addons$ = this.store.select(fromProcess.selectProcessAddons);
  savedCalculationState$ = this.store.select(fromProcess.selectProcessSavedCalculationState);
  savedCalculationError$ = this.store.select(fromProcess.selectProcessSavedCalculationError);
  selectProcessHashCode$ = this.store.select(fromProcess.selectProcessHashCode);
  packageName$ = this.store.select(fromProcess.selectProcessPackageName);
  sendStaticAttachmentsState$ = this.store.select(fromProcess.selectSendStaticAttachmentsState);
  sendStaticAttachmentsStatus$ = this.store.select(fromProcess.selectSendStaticAttachmentsStatus);
  selectedAgentSection$ = this.store.select(fromProcess.selectAgentSection);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  sendBackOfficeData$ = this.store.select(fromProcess.selectSendBackOfficeData);

  horizontalButtons: HorizontalButton[] = this.isTravelAgency
    ? [
        {
          label: 'conclusion_agent.payments.options.payment_to_agent',
          ndbxIcon: 'product-money-banknote',
          value: FormOfPayment.Cash,
        },
      ]
    : [
        {
          label: 'conclusion_agent.payments.options.cash',
          ndbxIcon: 'product-money-banknote',
          value: FormOfPayment.Cash,
        },
        {
          label: 'conclusion_agent.payments.options.card',
          ndbxIcon: 'product-credit-card',
          value: FormOfPayment.CreditCard,
        },
        {
          label: 'conclusion_agent.payments.options.bank_transfer',
          qbeIcon: QbeIcons.MoneyWallet,
          value: FormOfPayment.BankTransfer,
        },
      ];

  agreement = false;
  agreementFull = false;
  apiState = ApiState;
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private processService: ProcessService,
    private formsService: FormsService,
  ) {}

  get downloadAllDocuments$(): Observable<string | undefined> {
    return combineLatest([this.selectProcessHashCode$, this.processKind$]).pipe(
      map(([hashCode, processKind]) =>
        hashCode && processKind
          ? `${environment.baseApiUrl}globapi/packages/AllianzTravel/attachments/zip/${processKind}?hashCode=${hashCode}`
          : undefined,
      ),
      first(),
    );
  }

  ngOnInit(): void {
    this.store.dispatch(fromProcess.actions.setStep({ step: 7 }));
    this.processService.onPageInit();

    combineLatest([this.conclusionStatus$, this.nextStepUrl$])
      .pipe(
        takeUntil(this.ngDestroy$),
        filter(([apiState]) => apiState === ApiState.success),
      )
      .subscribe(([_, nextStepUrl]) => {
        void this.router.navigate(nextStepUrl);
      });

    this.saveCalculation();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  toggleAgreement() {
    this.agreementFull = !this.agreementFull;
  }

  sendEmail() {
    this.selectProcessHashCode$.subscribe((hashCode) => {
      if (!hashCode) {
        return;
      }
      this.store.dispatch(fromProcess.actions.sendStaticAttachmentsStart({ hashCode }));
    });
  }

  saveCalculation() {
    combineLatest([
      this.processKind$,
      this.selectedPackage$,
      this.addons$,
      this.selectedAgentSection$,
      this.discountCode$,
      this.selectProcessHashCode$,
      this.sendBackOfficeData$,
    ])
      .pipe(first())
      .subscribe(([processKind, packageCodeName, selectedAddons, selectedAgentSection, discountCode, hashCode, sendBackOfficeData]) => {
        let path: PathPayload;

        switch (processKind) {
          case ProcessType.TRIPABROAD:
            path = {
              tripAbroad: this.formsService.tripAbroadApi,
            };
            break;
          case ProcessType.TRAVELINPL:
            path = {
              travelInPL: this.formsService.travelInPlApi,
            };
            break;
          case ProcessType.RESIGNATION:
            path = {
              resignation: this.formsService.resignationApi,
            };
            break;
          default:
            throw new Error('Unknown process kind');
        }

        const agreements: number[] = [];

        const marketing = this.formsService.agreementsForm.value.marketing as Record<string, boolean>;
        const statements = this.formsService.agreementsForm.value.statements as Record<string, boolean>;

        Object.entries(marketing)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        Object.entries(statements)
          .filter(([_, value]) => value)
          .forEach(([key]) => {
            const agreementsId = Number(key.replace('agreement.', ''));

            agreements.push(agreementsId);
          });

        const payload: AgentSaveCalculationPayload = {
          path,
          policyholder: this.formsService.policyholderApi,
          insureds: this.formsService.insuredsApi,
          backofficeSection: sendBackOfficeData ? this.formsService.backOfficeSectionApi : null,
          selectedOffert: {
            packageCodeName,
            addOns: selectedAddons ?? {},
          },
          ...this.processService.apiPayload,
          discountCode: {
            code: discountCode || '',
          },
          ...(!(selectedAgentSection.premiumPercentIncrease === undefined || selectedAgentSection.premiumPercentIncrease === null)
            ? {
                agentSection: selectedAgentSection,
              }
            : {}),

          agreements,
          agentCommissionSplits:
            this.formsService.agentCommissionSplits.controls.splits.length > MIN_SPLITS_REQUIRED
              ? this.formsService.agentCommisionSplitsApi
              : null,
        };

        if (!hashCode) {
          this.store.dispatch(fromProcess.actions.saveCalculationStart({ payload }));
        }
      });
  }

  handlePaymentChange({ value }: RadioButton) {
    this.store.dispatch(fromProcess.actions.setFormOfPayment({ formOfPayment: value as FormOfPayment }));
  }

  closeSendStaticAttachmentsNotification() {
    this.store.dispatch(fromProcess.actions.sendStaticAttachmentsReset());
  }
}
